import React, { useState } from 'react';
import type { MessageKindAdapter, AdapterContext } from './types';
import MessageItem from '../components/MessageItem';
import { formatDate } from '../../../utils/datetime';
import { formatWon } from '../../../utils/number';

// Type 3 (바터) 새 모델은 슬롯 기준이라 영상 입력 자체가 없음. 수락은 단순 confirm + POST /accept.
// (옛 v1.0 의 Type3AcceptForm — recipient 측 file_hash picker — 는 폐기.)

/**
 * 마켓 제안 (Type 1 스크린 / Type 2 광고 / Type 3 바터) 어댑터.
 *
 * accept/reject/counter 는 기존 /market/proposals/:hash/* 엔드포인트를 호출.
 * 이 라우트들은 (a) NoEmptySlotError 시 자동 거절, (b) child proposal_hash 발급,
 * (c) conversations.context_ref 갱신 + message append 까지 포함된 운영 검증된 흐름.
 * 통합 PATCH /:cid/status 로 합치는 작업은 NoEmptySlot 자동 거절 정책 이식이 필요해
 * 별도 cleanup 단계로 미룸 — 어댑터는 facade 호출만.
 */

interface ProposalContext {
 proposal_type: 1 | 2 | 3;
 proposal_hash: string;
 latest_proposal_hash?: string;
 listing_hash?: string;
 sender_peer: string;
 recipient_peer: string;
 file_hash?: string;
 period_start: string;
 period_end: string;
 price_per_impression: number;
 media_family_hash?: string;
 recipient_family_hash?: string;
 advertiser_family_hash?: string;
}

const TYPE_LABEL: Record<1 | 2 | 3, string> = {
 1: '스크린 제안',
 2: '광고 제안',
 3: '바터 제안',
};

const fmtDate = (s?: string) => formatDate(s);

const ProposalOfferCard: React.FC<{ payload: any; ctx: ProposalContext }> = ({ payload, ctx }) => (
 <div className="text-sm space-y-1">
  <div><span className="text-gray-500">유형:</span> <span className="font-medium">{TYPE_LABEL[ctx.proposal_type]}</span></div>
  <div><span className="text-gray-500">기간:</span> {fmtDate(payload.period_start ?? ctx.period_start)} ~ {fmtDate(payload.period_end ?? ctx.period_end)}</div>
  <div><span className="text-gray-500">단가:</span> <span className="font-medium">{formatWon(payload.price_per_impression ?? ctx.price_per_impression)}/노출</span></div>
  {(payload.file_hash || ctx.file_hash) && (
   <div className="text-xs text-gray-500">영상: {(payload.file_hash || ctx.file_hash).slice(0, 16)}…</div>
  )}
 </div>
);

// ── 액션 패널 (accept/reject/counter) ────────────────────────────────────

const ActionPanel: React.FC<{ ctx: AdapterContext }> = ({ ctx }) => {
 const pctx = ctx.conv.context_ref as unknown as ProposalContext;
 const isOpen = ctx.conv.status === 'pending' || ctx.conv.status === 'countered';

 // 카운터 발송 권한: 양쪽 모두 가능. accept/reject 권한: '나의 차례' 인 사람.
 // 마지막 메시지 sender 가 상대방이면 viewer 가 응답할 차례.
 const myParticipant = ctx.participants.find(p =>
  !p.revoked_at && (p.peer_hash === ctx.viewer.peer_hash || p.acting_peer_hash === ctx.viewer.peer_hash),
 );
 const lastNonSysMessage = [...ctx.messages].reverse().find(m => m.message_type !== 'system_notice');
 const itsMyTurn = lastNonSysMessage && lastNonSysMessage.sender_peer_hash !== ctx.viewer.peer_hash;
 const canAct = !!myParticipant && isOpen && itsMyTurn;

 const [showCounter, setShowCounter] = useState(false);
 const [counter, setCounter] = useState({
  period_start: pctx.period_start,
  period_end: pctx.period_end,
  price_per_impression: pctx.price_per_impression,
  message: '',
 });

 if (!canAct) return null;

 const proposalHash = pctx.latest_proposal_hash || pctx.proposal_hash;

 const onAccept = async () => {
  const confirmMsg = pctx.proposal_type === 3
   ? '바터 매칭을 수락하시겠습니까? 양측 슬롯이 짝에게 독점 위임됩니다 (가상 모패밀리 자동 생성).'
   : '이 제안을 수락하시겠습니까?';
  if (!confirm(confirmMsg)) return;
  ctx.setBusy(true);
  try {
   const r = await fetch(`/market/proposals/${proposalHash}/accept`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
   });
   const j = await r.json();
   if (!j.success) throw new Error(j.error || '수락 실패');
   await ctx.onChange();
  } catch (e: any) {
   alert(e.message ?? '수락 실패');
  } finally {
   ctx.setBusy(false);
  }
 };

 const onReject = async () => {
  if (!confirm('이 제안을 거절하시겠습니까?')) return;
  ctx.setBusy(true);
  try {
   const r = await fetch(`/market/proposals/${proposalHash}/reject`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
   });
   const j = await r.json();
   if (!j.success) throw new Error(j.error || '거절 실패');
   await ctx.onChange();
  } catch (e: any) {
   alert(e.message ?? '거절 실패');
  } finally {
   ctx.setBusy(false);
  }
 };

 const onCounter = async () => {
  if (!counter.period_start || !counter.period_end || !counter.price_per_impression) {
   alert('기간과 단가를 모두 입력하세요.');
   return;
  }
  ctx.setBusy(true);
  try {
   const r = await fetch(`/market/proposals/${proposalHash}/counter`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
     payload: {
      file_hash: pctx.file_hash,
      period_start: counter.period_start,
      period_end: counter.period_end,
      price_per_impression: counter.price_per_impression,
      message: counter.message,
     },
    }),
   });
   const j = await r.json();
   if (!j.success) throw new Error(j.error || '역제안 실패');
   setShowCounter(false);
   setCounter(prev => ({ ...prev, message: '' }));
   await ctx.onChange();
  } catch (e: any) {
   alert(e.message ?? '역제안 실패');
  } finally {
   ctx.setBusy(false);
  }
 };

 return (
  <div className="mb-4 border border-gray-200 p-4 bg-gray-50">
   <div className="text-sm font-semibold mb-3">{TYPE_LABEL[pctx.proposal_type]} — {ctx.conv.status === 'pending' ? '내 응답 차례' : '역제안 응답 차례'}</div>

   {showCounter ? (
    <>
     <div className="grid grid-cols-2 gap-2 mb-3">
      <div>
       <label className="block text-xs text-gray-600 mb-1">시작</label>
       <input
        type="date"
        value={counter.period_start}
        onChange={e => setCounter({ ...counter, period_start: e.target.value })}
        disabled={ctx.busy}
        className="w-full px-2 py-1 border text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
       />
      </div>
      <div>
       <label className="block text-xs text-gray-600 mb-1">종료</label>
       <input
        type="date"
        value={counter.period_end}
        onChange={e => setCounter({ ...counter, period_end: e.target.value })}
        disabled={ctx.busy}
        className="w-full px-2 py-1 border text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
       />
      </div>
     </div>
     <div className="mb-3">
      <label className="block text-xs text-gray-600 mb-1">단가 (원/노출)</label>
      <input
       type="number"
       min={0}
       step={1}
       value={counter.price_per_impression}
       onChange={e => setCounter({ ...counter, price_per_impression: parseInt(e.target.value) || 0 })}
       disabled={ctx.busy}
       className="w-full px-2 py-1 border text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
     </div>
     <div className="mb-3">
      <label className="block text-xs text-gray-600 mb-1">메시지 (선택)</label>
      <textarea
       rows={2}
       value={counter.message}
       onChange={e => setCounter({ ...counter, message: e.target.value })}
       disabled={ctx.busy}
       className="w-full px-2 py-1 border text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
     </div>
     <div className="flex gap-2">
      <button
       onClick={onCounter}
       disabled={ctx.busy}
       className="px-3 py-1 bg-orange-500 hover:bg-orange-600 text-white text-sm disabled:opacity-50"
      >역제안 보내기</button>
      <button
       onClick={() => setShowCounter(false)}
       disabled={ctx.busy}
       className="px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm"
      >취소</button>
     </div>
    </>
   ) : (
    <div className="flex gap-2">
     <button
      onClick={onAccept}
      disabled={ctx.busy}
      className="px-3 py-1 bg-green-500 hover:bg-green-600 text-white text-sm disabled:opacity-50"
     >수락</button>
     <button
      onClick={() => setShowCounter(true)}
      disabled={ctx.busy}
      className="px-3 py-1 bg-orange-500 hover:bg-orange-600 text-white text-sm disabled:opacity-50"
     >역제안</button>
     <button
      onClick={onReject}
      disabled={ctx.busy}
      className="px-3 py-1 bg-red-500 hover:bg-red-600 text-white text-sm disabled:opacity-50"
     >거절</button>
    </div>
   )}
  </div>
 );
};

// ── 어댑터 ──────────────────────────────────────────────────────────────

const proposalAdapter: MessageKindAdapter = {
 kind: 'proposal',
 renderMessage: (m, ctx) => {
  const pctx = ctx.conv.context_ref as unknown as ProposalContext;
  return (
   <MessageItem key={m.id} message={m} viewerPeerHash={ctx.viewer.peer_hash}>
    {m.body && <div className="text-sm whitespace-pre-wrap mb-1">{m.body}</div>}
    {(m.message_type === 'proposal_offer' || m.message_type === 'proposal_counter') && (
     <ProposalOfferCard payload={m.payload} ctx={pctx} />
    )}
    {m.message_type === 'proposal_accept' && (
     <div className="text-xs text-green-700">제안 수락 — 슬롯 점유 / 캠페인 생성 처리됨</div>
    )}
    {m.message_type === 'proposal_reject' && (
     <div className="text-xs text-red-700">제안 거절</div>
    )}
   </MessageItem>
  );
 },
 renderActionPanel: ctx => <ActionPanel ctx={ctx} />,
};

export default proposalAdapter;
